import React, { useState, useEffect, useMemo, useCallback } from "react";
import Highcharts from "highcharts/highstock";
import HighchartsReact from "highcharts-react-official";
import { TSerieTemporal } from "../../../../services/OmniaService";
import graphTheme from "../../../../graphTheme";

export type RangeButton = "1M" | "3M" | "6M" | "9M" | "1Y" | "2Y" | "3Y" | "YTD" | "TODO";

type THighstockRatiosProps = {
  serieTemporal: TSerieTemporal[];
  loading?: boolean;
  error?: string;
  period: RangeButton;
  onChange?: (value?: any) => any;
};

const BASE_CLASS = "highStockSimulation"

const HighstockSimulation: React.FC<THighstockRatiosProps> = ({
  serieTemporal,
  loading,
  error,
  period,
}) => {
  // @ts-ignore
  Highcharts.setOptions(graphTheme);

  const carteraData = useMemo(
    () => serieTemporal?.map(point => [new Date(point.date).getTime(), point.cartera]),
    [serieTemporal]
  );

  const modeloData = useMemo(
    () => serieTemporal?.map(point => [new Date(point.date).getTime(), point.modelo]),
    [serieTemporal]
  );

  const [chartOptions, setChartOptions] = useState<any>({
    title: { text: "Original vs Nuevo" },
    series: [],
    plotOptions: {
      series: {
        compare: "percent",
        showInNavigator: true
      }
    },
    rangeSelector: {
      enabled: false
    },
    credits: { enabled: false },
    yAxis: {
      labels: {
        // @ts-ignore
        formatter: function () {
            // @ts-ignore
            return (this.value > 0 ? " + " : "") + this.value + "%";
        }
      },
      resize: { enabled: true },
      title: { text: "" }
    },
    tooltip: {
    // @ts-ignore
      pointFormatter: function () {
        // @ts-ignore
        return `${this.series.name}: ${this.y ? Highcharts.numberFormat(this.y, 4, ",", ".") : ""} (${Highcharts.numberFormat(this.change, 2, ",", ".")}%)`;
      },
      valueSuffix: "",
      split: true
    },
    chart: {
      type: "line",
      backgroundColor: "none",
      height: 650,
    },
    legend: {
      enabled: true,
      layout: "horizontal",
      align: "center",
      verticalAlign: "bottom"
    }
  });

  useEffect(() => {
    setChartOptions((prev: any) => ({
      ...prev,
      series: [
        {
          name: "Original",
          data: modeloData,
          type: "line",
          color: "#4284E6"
        },
        {
          name: "Nuevo",
          data: carteraData,
          type: "line",
          color: "#CC214F"
        }
      ]
    }));
  }, [carteraData, modeloData]);

useEffect(() => {
    if (!modeloData || !carteraData) return
    const now = Date.now();
    let min: number | undefined;
    let max: number = now;

    const monthsToMs = (m: number) => 1000 * 60 * 60 * 24 * 30 * m;
    const yearsToMs = (y: number) => 1000 * 60 * 60 * 24 * 365 * y;

    if (period === "TODO" || period === "INCEPTION" as RangeButton) {
      const allTimestamps = [...modeloData, ...carteraData].map(([timestamp]) => timestamp);
      if (allTimestamps.length > 0) {
        min = Math.min(...allTimestamps);
        max = Math.max(...allTimestamps);
      } else {
        min = undefined;
        max = now;
      }
    } else {
      switch (period.toUpperCase()) {
        case "1M":
          min = now - monthsToMs(1);
          break;
        case "3M":
          min = now - monthsToMs(3);
          break;
        case "6M":
          min = now - monthsToMs(6);
          break;
        case "9M":
          min = now - monthsToMs(9);
          break;
        case "1Y":
          min = now - yearsToMs(1);
          break;
        case "2Y":
          min = now - yearsToMs(2);
          break;
        case "3Y":
          min = now - yearsToMs(3);
          break;
        case "YTD":
          min = new Date(new Date().getFullYear(), 0, 1).getTime();
          break;
        default:
          min = undefined;
      }
  }

  setChartOptions((prev: any) => ({
    ...prev,
    xAxis: {
      ...prev.xAxis,
      min,
      max
    }
  }));
}, [period, modeloData, carteraData]);


  if (loading) return <div>Cargando datos...</div>;
  if (error) return <div style={{ color: "red" }}>Error: {error}</div>;
  if (!serieTemporal || serieTemporal.length === 0) return <div>No hay datos para mostrar.</div>;

  return (
    <div className={BASE_CLASS}>
      <HighchartsReact
        key={(period == "INCEPTION" as RangeButton ? "all" : period)}
        highcharts={Highcharts}
        constructorType="stockChart"
        options={chartOptions}
        containerProps={{ style: { width: "100%", height: "100%" } }}
      />
    </div>
  );
};

export default HighstockSimulation;
